// src/auth/AuthShell.jsx
import { motion, MotionConfig } from "framer-motion";
import DriftingGlow from "../Animations/DriftingGlow";
import { formEnter as presetFormEnter } from "../Animations/motionpresets.js";

const BLUE = "#2A7EFA";
const CARD_BG = "bg-white/80";
const FRAME_RING = "ring-white/60";
const CARD_BORDER = "border-white/70";

const FALLBACK = {
  hidden: { opacity: 0, y: 12, scale: 0.995 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: "easeOut" }, // ⬅️ adjust duration
  },
};

/** Utility to pick variant keys safely */
function pickVariantKeys(v) {
  if (!v || typeof v !== "object") return { useDirect: true };
  const has = (k) => Object.prototype.hasOwnProperty.call(v, k);

  // Support multiple naming schemes:
  const initialKey = has("initial")
    ? "initial"
    : has("hidden")
    ? "hidden"
    : null;
  const animateKey = has("ready")
    ? "ready"
    : has("enter")
    ? "enter"
    : has("show")
    ? "show"
    : null;

  if (!initialKey || !animateKey) return { useDirect: true };
  return { useDirect: false, initialKey, animateKey };
}

export default function AuthShell({
  children,
  /** Optional: override variants from the outside if you want */
  variants = presetFormEnter,
  /** Optional: extra classes */
  className = "",
  cardClassName = "",
  bgClassName = "",
}) {
  const { useDirect, initialKey, animateKey } = pickVariantKeys(variants);

  return (
    <MotionConfig reducedMotion="never">
      <div
        className={[
          "min-h-screen grid place-items-center relative overflow-hidden px-4",
          // 🌈 Background (edit here)
          "bg-[radial-gradient(700px_350px_at_0%_0%,rgba(186,230,253,.45),transparent_60%),radial-gradient(800px_380px_at_100%_100%,rgba(199,210,254,.45),transparent_60%),linear-gradient(180deg,#f8fafc,#eef2ff)]",
          bgClassName,
          className,
        ].join(" ")}
      >
        {/* subtle glass frame */}
        <div className={`pointer-events-none absolute `} />

        {/* floating glows */}
        <DriftingGlow
          className="absolute -top-24 -left-24 bg-sky-200/50"
          size={420}
        />
        <DriftingGlow
          className="absolute -bottom-28 -right-24 bg-indigo-200/50 blur-[90px]"
          size={520}
          dx={[0, -8, 8, 0]}
          dy={[0, -6, 10, 0]}
          duration={16}
        />

        {/* 🧊 Card */}
        {useDirect ? (
          /* Fallback explicit animation (never gets stuck) */
          <motion.div
            initial={FALLBACK.hidden}
            animate={FALLBACK.show}
            className={[
              "w-full max-w-md rounded-3xl border",
              CARD_BORDER,
              CARD_BG,
              "backdrop-blur-xl p-6 sm:p-8",
              "shadow-[0_20px_60px_rgba(67,56,202,.12),0_10px_30px_rgba(2,6,23,.06)]",
              cardClassName,
            ].join(" ")}
          >
            {children}
          </motion.div>
        ) : (
          /* Use provided variants when keys are valid */
          <motion.div
            variants={variants}
            initial={initialKey}
            animate={animateKey}
            className={[
              "w-full max-w-md rounded-3xl border",
              CARD_BORDER,
              CARD_BG,
              "backdrop-blur-xl p-6 sm:p-8",
              "shadow-[0_20px_60px_rgba(67,56,202,.12),0_10px_30px_rgba(2,6,23,.06)]",
              cardClassName,
            ].join(" ")}
          >
            {children}
          </motion.div>
        )}
      </div>
    </MotionConfig>
  );
}

/** Export brand color if you want to reuse in child screens */
export const AUTH_BRAND_BLUE = BLUE;
